import React from 'react';
import useNearViewport from '../../shared/hooks/useNearViewport';
import { getCardImageUrl, handleCardImageError } from '../../shared/media/cardMedia';
import { Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Stack,
  Typography
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HeroSection from '../../components/HeroSection';
import { dedupeNewsByEditorialContent } from '../../utils/newsDedupe';

const toText = (value = '') =>
  String(value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const HomeMobile = ({ showImpactSection, news, engines, impactData, enginesTitle }) => {
  const [newsRef, loadMedia] = useNearViewport();
  const topNews = dedupeNewsByEditorialContent(news).slice(0, 4);

  return (
    <>
      <HeroSection />

      <Box ref={newsRef} id="news-grid" sx={{ py: 3, bgcolor: '#f5f5f5', scrollMarginTop: '72px' }}>
        <Container maxWidth="sm" sx={{ px: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Noticias
            </Typography>
            <Button component={RouterLink} to="/news" endIcon={<ArrowForwardIcon />} sx={{ minWidth: 0, px: 1 }}>
              Ver todas
            </Button>
          </Stack>

          <Stack spacing={2}>
            {topNews.map((item) => {
              const subtitle = toText(item.subtitle || '');
              return (
                <Card key={item.id} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <CardActionArea component={RouterLink} to={`/news/${item.id}`}>
                    <CardMedia
                      component="img"
                      src={loadMedia ? getCardImageUrl(item.image || '/images/hero.png') : undefined}
                      loading="lazy"
                      decoding="async"
                      onError={e => handleCardImageError(e, item.image)}
                      alt={item.title || 'Noticia'}
                      sx={{ height: 180, objectFit: 'cover' }}
                    />
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        ID {String(item.id).padStart(4, '0')}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25, mb: subtitle ? 1 : 0 }}>
                        {item.title}
                      </Typography>
                      {!!subtitle && (
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          {subtitle}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Stack>
        </Container>
      </Box>

      <Box id="o-que-fazemos" sx={{ py: 3.5, bgcolor: '#0b1016', color: '#fff', scrollMarginTop: '72px' }}>
        <Container maxWidth="sm" sx={{ px: 2 }}>
          <Typography variant="overline" sx={{ letterSpacing: '0.12em', color: 'rgba(255,255,255,0.64)', fontWeight: 700 }}>
            O_QUE_FAZEMOS
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.65, color: '#fff' }}>
            O que fazemos
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.7, mb: 2.1 }}>
            {enginesTitle || 'Nossos Motores de Desenvolvimento'}
          </Typography>

          <Stack spacing={1.25}>
            {(engines || []).map((engine) => (
              <Accordion
                key={engine.id || engine.number}
                disableGutters
                sx={{
                  bgcolor: '#111821',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.28)',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    borderColor: 'rgba(204,0,0,0.85)'
                  }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
                      {engine.title}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.80)', lineHeight: 1.7, mb: 1.25 }}>
                    {engine.description}
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {(engine.capabilities || []).map((cap) => (
                      <Chip
                        key={`${engine.id}-${cap}`}
                        label={cap}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.08)',
                          color: '#f3f4f6',
                          borderRadius: 1,
                          border: '1px solid rgba(255,255,255,0.18)'
                        }}
                      />
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Container>
      </Box>

      {showImpactSection && (
        <Box id="ciencia-tecnologia" sx={{ py: 3.5, bgcolor: '#fff', scrollMarginTop: '72px' }}>
          <Container maxWidth="sm" sx={{ px: 2 }}>
            <Typography variant="overline" sx={{ letterSpacing: '0.14em', color: 'text.secondary' }}>
              CIENCIA & TECNOLOGIA
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
              {impactData?.title || 'Pesquisa & Inovacao'}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 2.25 }}>
              {toText(impactData?.description || '')}
            </Typography>

            <Button
              component={RouterLink}
              to="/visao-geral"
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardIcon />}
              sx={{ borderRadius: 1.25, px: 2.25, py: 1.1 }}
            >
              Explorar abordagem
            </Button>
          </Container>
        </Box>
      )}
    </>
  );
};

export default HomeMobile;
